import { defineStore } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';

export const useSkippedModulesStore = defineStore('skippedModules', {
  state: () => {
    // Check if we're coming back after an unskip operation
    const unskipFlag = localStorage.getItem('unskipOperation');
    if (unskipFlag) {
      localStorage.removeItem('unskipOperation');
      localStorage.removeItem('skipped_modules');
    }
    
    // Try to load from localStorage first if available
    const savedData = localStorage.getItem('skipped_modules');
    let initialState = {
      skippedRoutes: [],
      loading: false,
      error: null,
      initialized: false,
      lastFetched: null
    };
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const timestamp = parsed.timestamp || 0;
        const now = new Date().getTime();
        const cacheAge = now - timestamp;
        const cacheExpiryTime = 30 * 60 * 1000; // 30 minutes
        
        // Only use cache if it's not too old
        if (Array.isArray(parsed.skippedRoutes) && cacheAge < cacheExpiryTime) {
          initialState = {
            ...initialState,
            skippedRoutes: parsed.skippedRoutes,
            initialized: true,
            lastFetched: timestamp
          };
        }
      } catch (e) {
        // Silently handle any parsing errors
      }
    }
    
    return initialState;
  },

  actions: {
    reset() {
      this.skippedRoutes = [];
      this.loading = false;
      this.error = null;
      this.initialized = false;
      this.lastFetched = null;
      
      // Clear the localStorage cache
      localStorage.removeItem('skipped_modules');
    },

    async initializeOnLogin() {
      // Check if already initialized with valid data
      if (this.initialized && Array.isArray(this.skippedRoutes) && this.skippedRoutes.length > 0) {
        return;
      }
            
      // Always mark as not initialized until we get fresh data
      this.initialized = false;
      
      try {
        // Fetch fresh data with a short timeout to ensure token is ready
        await new Promise(resolve => setTimeout(resolve, 300));
        const result = await this.fetchSkippedModules(true);
        
        // Check for any pending redirects from previous skips
        this.checkSkipState();
        
        return result;
      } catch (error) {
        return [];
      }
    },

    // Check for any pending skip states that need to be processed
    checkSkipState() {
      // Check for pending redirects - we no longer use this for navigation,
      // but we'll clean up any existing flags
      const pendingRedirect = localStorage.getItem('pendingRedirect');
      if (pendingRedirect) {
        // Just clear the flag, no need to redirect, Vue Router handles navigation
        localStorage.removeItem('pendingRedirect');
        
        // Dispatch an event that App.vue can listen to if needed
        window.dispatchEvent(new CustomEvent('pending-redirect-cleared', {
          detail: { 
            path: pendingRedirect,
            timestamp: Date.now()
          }
        }));
      }
      
      // Check for recent skip/unskip operations that might have been interrupted
      const skipTimestamp = localStorage.getItem('skipTimestamp');
      const unskipTimestamp = localStorage.getItem('unskipTimestamp');
      
      if (skipTimestamp && unskipTimestamp) {
        // If both exist, remove the older one
        const skipTime = parseInt(skipTimestamp, 10);
        const unskipTime = parseInt(unskipTimestamp, 10);
        
        if (skipTime > unskipTime) {
          // Skip is more recent, remove unskip flags
          localStorage.removeItem('unskipTimestamp');
          localStorage.removeItem('unskippedModules');
        } else {
          // Unskip is more recent, remove skip flags
          localStorage.removeItem('skipTimestamp');
          localStorage.removeItem('forcedSkip');
        }
      }
      
      // Clean up any stale operations (more than 5 minutes old)
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (skipTimestamp && (now - parseInt(skipTimestamp, 10)) > fiveMinutes) {
        localStorage.removeItem('skipTimestamp');
        localStorage.removeItem('forcedSkip');
      }
      
      if (unskipTimestamp && (now - parseInt(unskipTimestamp, 10)) > fiveMinutes) {
        localStorage.removeItem('unskipTimestamp');
        localStorage.removeItem('unskippedModules');
      }
    },

    // Helper method to parse API response data in various formats
    parseApiResponse(data) {
      // Case 1: Nested format {data: {skipped_routes: []}}
      if (data?.data?.skipped_routes && Array.isArray(data.data.skipped_routes)) {
        return data.data.skipped_routes;
      }
      
      // Case 2: Direct format {skipped_routes: []}
      if (data?.skipped_routes && Array.isArray(data.skipped_routes)) {
        return data.skipped_routes;
      }
      
      // Case 3: Empty response (likely when unskipping) - return empty array
      return [];
    },

    async fetchSkippedModules(force = false) {
      // Check for forced refresh flag
      const forceRefreshSkippedModules = localStorage.getItem('forceRefreshSkippedModules');
      if (forceRefreshSkippedModules) {
        localStorage.removeItem('forceRefreshSkippedModules');
        force = true;
      }
      
      // Check for forced refresh all data flag 
      const forceRefreshAllData = localStorage.getItem('forceRefreshAllData');
      if (forceRefreshAllData) {
        localStorage.removeItem('forceRefreshAllData');
        force = true;
      }
      
      // Check for recent skip operation
      const lastSkipOperation = localStorage.getItem('lastSkipOperation');
      if (lastSkipOperation) {
        const now = Date.now();
        const lastSkipTime = parseInt(lastSkipOperation, 10);
        
        // If skip operation was in the last 5 seconds, force refresh
        if (now - lastSkipTime < 5000) {
          force = true;
        } else {
          // Remove old timestamp
          localStorage.removeItem('lastSkipOperation');
        }
      }
      
      // Always fetch data if it's the first time after a new login session
      const forceRefresh = force || !this.initialized;
      
      // If already loaded and not forced, use cached data
      if (this.initialized && !forceRefresh) {
        return this.skippedRoutes;
      }

      this.loading = true;
      this.error = null;
      const authStore = useAuthStore();
      
      // Check if auth token is available
      if (!authStore.user?.accessToken) {
        this.loading = false;
        
        // If no token but we have cached data, use that
        const savedData = localStorage.getItem('skipped_modules');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            if (Array.isArray(parsed.skippedRoutes)) {
              this.skippedRoutes = parsed.skippedRoutes;
              this.initialized = true;
              this.lastFetched = parsed.timestamp || Date.now();
              return this.skippedRoutes;
            }
          } catch (e) {
            // Silent handling
          }
        }
        
        return [];
      }
      
      try {
        const response = await fetch(`${global.API_URL}/extensions/skipped_modules`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        
        // Parse the API response to get the skipped routes
        let skippedRoutes = this.parseApiResponse(data);
        
        // Check if we have recently unskipped specific modules
        const unskippedModulesJson = localStorage.getItem('unskippedModules');
        const recentUnskipTime = parseInt(localStorage.getItem('unskipTimestamp'), 10);
        
        // Only apply the override if we have both a list of unskipped modules and a recent timestamp
        if (unskippedModulesJson && recentUnskipTime) {
          const now = Date.now();
          const fiveMinutesInMs = 5 * 60 * 1000;
          
          // Only apply the override if the unskip was recent (within 5 minutes)
          if ((now - recentUnskipTime) < fiveMinutesInMs) {
            try {
              const unskippedModules = JSON.parse(unskippedModulesJson);
              
              if (Array.isArray(unskippedModules) && unskippedModules.length > 0) {
                // Filter out the specifically unskipped modules from the API response
                skippedRoutes = skippedRoutes.filter(route => 
                  !unskippedModules.includes(route)
                );
              }
            } catch (e) {
              // Silent error handling
              console.warn('Error parsing unskipped modules:', e);
            }
          } else {
            // Clean up old unskip data if it's more than 5 minutes old
            localStorage.removeItem('unskippedModules');
            localStorage.removeItem('unskipTimestamp');
          }
        }
        
        // Check for forced skip operations
        const forcedSkip = localStorage.getItem('forcedSkip');
        if (forcedSkip) {
          // Recent skip operation, so ensure we still have the skipped modules
          // Get current cached skipped modules
          try {
            const cachedData = localStorage.getItem('skipped_modules');
            if (cachedData) {
              const parsed = JSON.parse(cachedData);
              if (Array.isArray(parsed.skippedRoutes) && parsed.skippedRoutes.length > 0) {
                // Add any cached routes that might be missing from the API response
                parsed.skippedRoutes.forEach(route => {
                  if (!skippedRoutes.includes(route)) {
                    skippedRoutes.push(route);
                  }
                });
              }
            }
          } catch (e) {
            console.warn('Error processing skipped modules cache:', e);
          }
          
          // Clear the flag
          localStorage.removeItem('forcedSkip');
        }
        
        // Set the potentially filtered routes
        this.skippedRoutes = skippedRoutes;
        this.initialized = true;
        this.lastFetched = new Date().getTime();
        
        // Save to localStorage for faster future loads
        localStorage.setItem('skipped_modules', JSON.stringify({
          skippedRoutes: this.skippedRoutes,
          timestamp: this.lastFetched
        }));
        
        return this.skippedRoutes;
      } catch (error) {
        console.error('Error fetching skipped modules:', error);
        this.error = error.message;
        
        // Try to load from localStorage if API fails
        const savedData = localStorage.getItem('skipped_modules');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            if (Array.isArray(parsed.skippedRoutes)) {
              this.skippedRoutes = parsed.skippedRoutes;
              this.initialized = true;
              this.lastFetched = parsed.timestamp || new Date().getTime();
              return this.skippedRoutes;
            }
          } catch (e) {
            // Silent error handling
            console.warn('Error parsing cached skipped modules:', e);
          }
        }
        
        this.skippedRoutes = [];
        return [];
      } finally {
        this.loading = false;
      }
    },

    async updateSkippedModules(routesToSkip) {
      this.loading = true;
      this.error = null;
      const authStore = useAuthStore();
            
      // Ensure routesToSkip is an array
      const skippedRoutes = Array.isArray(routesToSkip) ? [...routesToSkip] : [];
      
      // Check if this is a skip or unskip operation
      const isSkipOperation = localStorage.getItem('forcedSkip') === 'true';
      const isUnskipOperation = localStorage.getItem('unskipOperation') === 'true';
      
      // Clear operation flags immediately to prevent conflicts
      if (isSkipOperation) {
        localStorage.removeItem('forcedSkip');
      }
      
      if (isUnskipOperation) {
        localStorage.removeItem('unskipOperation');
      }
      
      try {
        // Check if auth token is available
        if (!authStore.user?.accessToken) {
          this.loading = false;
          throw new Error("Authentication token not available");
        }
        
        // Force refresh to get latest state before update
        await this.fetchSkippedModules(true);
        
        // Store original skipped routes for reference
        const originalSkippedRoutes = [...this.skippedRoutes];
        
        // Update localStorage flags
        localStorage.setItem('forceRefreshSkippedModules', 'true');
        localStorage.setItem('lastSkipOperation', Date.now().toString());
        
        // Update our local state first
        this.skippedRoutes = [...skippedRoutes];
        
        // Force update localStorage immediately to ensure consistency
        localStorage.setItem('skipped_modules', JSON.stringify({
          skippedRoutes: this.skippedRoutes,
          timestamp: Date.now()
        }));
        
        // Make the API call
        const response = await fetch(`${global.API_URL}/extensions/skipped_modules`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate'
          },
          body: JSON.stringify({
            skipped_routes: skippedRoutes
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        
        // Parse the API response
        const parsedRoutes = this.parseApiResponse(data);
        
        // For unskip operations, ensure specific modules are removed
        if (isUnskipOperation) {
          const unskippedModulesJson = localStorage.getItem('unskippedModules');
          if (unskippedModulesJson) {
            try {
              const unskippedModules = JSON.parse(unskippedModulesJson);
              
              // Verify the modules were actually unskipped in the API response
              const stillSkipped = unskippedModules.filter(route => parsedRoutes.includes(route));
              
              if (stillSkipped.length > 0) {
                console.warn('Some modules were not unskipped in API response, forcing local override');
                // Force remove these modules from our local state
                this.skippedRoutes = this.skippedRoutes.filter(
                  route => !unskippedModules.includes(route)
                );
              } else {
                // API response looks good, use it
                this.skippedRoutes = parsedRoutes;
              }
            } catch (e) {
              console.error('Error processing unskipped modules:', e);
              // Fallback to API response
              this.skippedRoutes = parsedRoutes;
            }
          } else {
            // No specific modules to unskip, use API response
            this.skippedRoutes = parsedRoutes;
          }
        } else if (isSkipOperation) {
          // For skip operations, ensure our local state has the skipped routes
          const missingRoutes = skippedRoutes.filter(route => !parsedRoutes.includes(route));
          
          if (missingRoutes.length > 0) {
            console.warn('API response missing some skipped routes, using local state');
            // Keep our current skippedRoutes which should include all required routes
          } else {
            // API response looks good, use it
            this.skippedRoutes = parsedRoutes;
          }
        } else {
          // For normal operations, use the API response
          this.skippedRoutes = parsedRoutes;
        }
        
        // Mark as initialized since we've successfully updated
        this.initialized = true;
        this.lastFetched = new Date().getTime();
        
        // Final save to localStorage
        localStorage.setItem('skipped_modules', JSON.stringify({
          skippedRoutes: this.skippedRoutes,
          timestamp: this.lastFetched
        }));
        
        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('skipped-modules-updated', {
          detail: {
            skippedRoutes: this.skippedRoutes,
            operation: isUnskipOperation ? 'unskip' : 'skip',
            timestamp: Date.now()
          }
        }));
        
        return this.skippedRoutes;
      } catch (error) {
        console.error('Error in updateSkippedModules:', error);
        this.error = error.message;
        
        // Return the current state
        return this.skippedRoutes;
      } finally {
        this.loading = false;
      }
    },

    // Dedicated method for unskipping all modules
    async unskipAllModules() {
      // First ensure we have the latest state from the server
      await this.fetchSkippedModules(true);
      
      // Only proceed if we actually have something to unskip
      if (!Array.isArray(this.skippedRoutes) || this.skippedRoutes.length === 0) {
        return true;
      }
      
      // Get the specific modules that are being unskipped (should be set by the caller)
      let modulesToUnskip = [];
      try {
        const unskippedModulesJson = localStorage.getItem('unskippedModules');
        if (unskippedModulesJson) {
          modulesToUnskip = JSON.parse(unskippedModulesJson);
        } else {
          // If no specific modules are specified, unskip all
          modulesToUnskip = [...this.skippedRoutes];
          localStorage.setItem('unskippedModules', JSON.stringify(modulesToUnskip));
        }
      } catch (e) {
        modulesToUnskip = [...this.skippedRoutes];
      }
      
      // Clear local cache to force a fresh fetch next time
      localStorage.removeItem('skipped_modules');
      
      try {
        // Immediately update local state to reflect the change
        // Filter out the specific modules that are being unskipped
        const previousSkippedRoutes = [...this.skippedRoutes];
        this.skippedRoutes = this.skippedRoutes.filter(route => !modulesToUnskip.includes(route));
        
        // Execute the API call to clear skipped modules
        const authStore = useAuthStore();
        if (!authStore.user?.accessToken) {
          throw new Error('No authentication token available');
        }
        
        // Make the API call to update the skipped modules
        const response = await fetch(`${global.API_URL}/extensions/skipped_modules`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store'
          },
          body: JSON.stringify({
            skipped_routes: this.skippedRoutes
          })
        });
        
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        
        // Parse the response to see what the server returned
        const data = await response.json();
        
        // Update our store with the parsed response
        const parsedRoutes = this.parseApiResponse(data);
        
        // If the server response includes any of the modules we just unskipped, 
        // force the local state to exclude them regardless
        if (parsedRoutes.some(route => modulesToUnskip.includes(route))) {
          this.skippedRoutes = parsedRoutes.filter(route => !modulesToUnskip.includes(route));
        } else {
          // Otherwise just use the server response
          this.skippedRoutes = parsedRoutes;
        }
        
        this.initialized = true;
        this.lastFetched = new Date().getTime();
        
        // Save the updated state to localStorage
        localStorage.setItem('skipped_modules', JSON.stringify({
          skippedRoutes: this.skippedRoutes,
          timestamp: this.lastFetched
        }));
        
        return true;
      } catch (error) {
        // Even if API fails, ensure the specified modules are unskipped locally
        this.skippedRoutes = this.skippedRoutes.filter(route => !modulesToUnskip.includes(route));
        this.initialized = true;
        
        // Set an error flag that can be checked by the UI
        localStorage.setItem('unskipError', error.message || 'Unknown error during unskip');
        
        // Rethrow for caller handling
        throw error;
      }
    },

    isRouteSkipped(route) {
      if (!Array.isArray(this.skippedRoutes) || typeof route !== 'string') {
        return false;
      }
      
      // Special case: Module 9 overview should not be considered skipped
      if (route === '/steps/funding-new-projects/overview') {
        return false;
      }
      
      // Check if the exact route is skipped or if it's part of a skipped module
      return this.skippedRoutes.some(skippedRoute => {
        // Check for exact match
        if (skippedRoute === route) return true;
        // Check if the current route is part of a skipped module path
        const moduleBase = route.split('/').slice(0, 3).join('/');
        return skippedRoute === moduleBase;
      });
    },

    async unskipModule(modulePath) {
      if (!modulePath) {
        throw new Error('No module path provided for unskipping');
      }
      
      // Check if we have an auth token using the auth store
      const authStore = useAuthStore();
      if (!authStore.user || !authStore.user.accessToken) {
        throw new Error('Authentication required for unskipping modules');
      }
      
      try {
        // Force a fresh fetch of skipped modules from the API to ensure we have the latest state
        await this.fetchSkippedModules(true);
        
        // Check if the module is currently skipped
        if (!this.skippedRoutes.includes(modulePath)) {
          return; // Nothing to do
        }
        
        // Initialize paths to unskip with the specified module
        let modulesToUnskip = [modulePath];
        

        
        // Remove these modules from the skipped routes array
        const updatedSkippedRoutes = this.skippedRoutes.filter(route => 
          !modulesToUnskip.includes(route)
        );
        
        // Immediately update local state to reflect the changes
        this.skippedRoutes = updatedSkippedRoutes;
        
        // Also update localStorage to ensure immediate local changes
        localStorage.setItem('skipped_modules', JSON.stringify({
          skippedRoutes: this.skippedRoutes,
          timestamp: Date.now()
        }));
        
        // Set a flag to force re-fetch on page load
        localStorage.setItem('forceRefreshSkippedModules', 'true');
        
        // Make API call to update skipped modules
        const response = await fetch(`${global.API_URL}/extensions/skipped_modules`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authStore.user.accessToken}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate'
          },
          body: JSON.stringify({
            skipped_routes: updatedSkippedRoutes
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API error: ${response.status}`);
        }
        
        // Parse the response
        const data = await response.json();
        
        const parsedData = this.parseApiResponse(data);
        
        // Check if API did not remove the modules we wanted to unskip
        const stillSkippedModules = modulesToUnskip.filter(path => 
          parsedData.includes(path)
        );
        
        if (stillSkippedModules.length > 0) {
          // Try a second API call with an explicit empty array if needed
          if (updatedSkippedRoutes.length === 0) {
            // Make a second API call with an explicit empty array
            const secondResponse = await fetch(`${global.API_URL}/extensions/skipped_modules`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authStore.user.accessToken}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate'
              },
              body: JSON.stringify({
                skipped_routes: []
              })
            });
            
            if (secondResponse.ok) {
              const secondData = await secondResponse.json();
              const secondParsedData = this.parseApiResponse(secondData);
              
              // Update with the second response data
              this.skippedRoutes = secondParsedData;
            }
          }
          
          // Regardless of the API response, force our local state
          this.skippedRoutes = updatedSkippedRoutes;
        } else {
          // Update local state with API response if it correctly removed the modules
          this.skippedRoutes = parsedData;
        }
        
        // Make sure localStorage is updated with our final state
        localStorage.setItem('skipped_modules', JSON.stringify({
          skippedRoutes: this.skippedRoutes,
          timestamp: Date.now()
        }));
        
        this.initialized = true;
        this.lastFetched = Date.now();
        
        // Update localStorage with all the modules we unskipped
        const unskippedModules = JSON.parse(localStorage.getItem('unskippedModules') || '[]');
        
        // Add all unskipped modules to the list if they're not already there
        modulesToUnskip.forEach(path => {
          if (!unskippedModules.includes(path)) {
            unskippedModules.push(path);
          }
        });
        
        localStorage.setItem('unskippedModules', JSON.stringify(unskippedModules));
        localStorage.setItem('unskipTimestamp', Date.now().toString());
        
        return this.skippedRoutes;
      } catch (error) {
        throw error;
      }
    }
  }
}); 